/* eslint-disable no-underscore-dangle */
import React, { useMemo } from 'react'
import PropTypes from 'prop-types'
import { FormLabel, Grid } from '@mui/material'
import { Form, Formik } from 'formik'
import clsx from 'clsx'
import { withStyles } from '~/themes/useStyles'
import style from './style'
import { getGalleryFileInfo } from '~/utils/helpers/media'
import { Field } from '~/components/Formik'
import InputTag from '~/components/common/InputTag'
import mediaApis from '~/services/apis/media'
import { showDialog } from '~/components/common/Dialog'
import { dialogParams } from '~/components/common/Dialog/CustomDialog'
import { useMedia } from '~/redux/hooks/media'
import TextInput from '~/components/common/TextInput'
import { FILE_INFO_MODE } from '~/constants/media'
import { TextBlockToolTip } from '~components/common/TextBlock'

const FileInfo = (props) => {
  const {
    classes,
    file,
    dimensions,
    renderSetProductButton,
    renderReplaceFileButton,
    didUpdatedFileMetadata,
    listFileSelected,
    enabledEditMetadataIds,
    mode,
    displayName,
  } = props
  const { name, fileType, uploadedOn, fileSize, id, id_select } =
    getGalleryFileInfo(file)
  const { mediaFile, mediaFolder, actions } = useMedia()

  const fileMetadata = useMemo(() => {
    if (listFileSelected) {
      const listFileObj = listFileSelected.reduce(
        (obj, _file) => ({
          ...obj,
          [_file.id_select || _file.id]: {
            ..._file,
            ...{
              title:
                _file.title || _file.title === '' ? _file.title : _file.name,
            },
            ...{ alternative_text: _file.alternative_text || _file.alt },
          },
        }),
        {},
      )
      return listFileObj
    }
    return {}
  }, [JSON.stringify(listFileSelected)])

  const onKeyDown = (keyEvent) => {
    if ((keyEvent.charCode || keyEvent.keyCode) === 13) {
      keyEvent.preventDefault()
    }
  }

  const isReadOnlyMetadata = (val) => !enabledEditMetadataIds.includes(val)

  const handleUpdateMetadata = async (values) => {
    const fileIds = Object.keys(values)
    actions.setListFileFolderData({
      files: mediaFile.list,
      folders: mediaFolder.list,
      loading: {
        updateMetadata: true,
      },
    })

    try {
      const metadataFiles = await Promise.all(
        Array.from(fileIds).map(async (fileId) =>
          mediaApis.updateFileMetadata({
            ...{ file_id: fileId },
            ...values[fileId],
          }),
        ),
      )

      actions.setListFileFolderData({
        loading: {
          updateMetadata: false,
        },
      })
      didUpdatedFileMetadata(metadataFiles)
    } catch (err) {
      showDialog({
        ...dialogParams.error(),
        ...{
          description: `${Object.keys(err)[0]} : ${err[Object.keys(err)[0]]}`,
        },
      })
      actions.setListFileFolderData({
        loading: {
          updateMetadata: false,
        },
      })
    }
  }

  const onSubmitForm = (values) => {
    if (mode === FILE_INFO_MODE.GALLERY_DIALOG) {
      const files = Object.values(values).map((e) => ({
        ...e,
        ...{ id: e.id_select || e._id || e.id },
      }))
      const newFilesSelected = [...listFileSelected]
      files.forEach((newFile) => {
        const fileIdx = newFilesSelected.findIndex(
          (fileSelect) =>
            (fileSelect.id_select || fileSelect.id) === newFile.id,
        )
        newFilesSelected[fileIdx] = {
          ...newFile,
          ...{ id: newFile.id, src: newFile.src || newFile.public_url },
        }
      })

      return didUpdatedFileMetadata(newFilesSelected)
    }
    return handleUpdateMetadata(values)
  }

  const renderInfo = () => (
    <Grid container>
      <Grid
        item
        sm={mode === FILE_INFO_MODE.GALLERY_DIALOG ? 12 : 6}
        md={6}
        style={{ paddingLeft: 10, paddingRight: 5 }}
      >
        <div className={classes.itemContainer}>
          <span className={clsx(classes.galleryInfoLabel, classes.fileData)}>
            File Name:
          </span>
          <TextBlockToolTip
            maxLine={1}
            style={{
              display: 'flex',
              alignItems: 'center',
            }}
            title={
              <span>
                {mode === FILE_INFO_MODE.GALLERY_DIALOG
                  ? 'No data'
                  : name || 'No data'}
              </span>
            }
          >
            <span
              data-tooltip
              style={{
                fontWeight: 400,
                display: 'inline-block',
                fontSize: 14,
                color: '#3B4757',
                marginTop: 8,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                paddingRight: 5,
              }}
            >
              {mode === FILE_INFO_MODE.GALLERY_DIALOG && !displayName
                ? 'No data'
                : name || 'No data'}
            </span>
          </TextBlockToolTip>
        </div>
        <div className={classes.itemContainer}>
          <span className={classes.galleryInfoLabel}>Uploaded On:</span>
          <span className={classes.galleryInfoValue}>{uploadedOn}</span>
        </div>
        <div className={classes.itemContainer}>
          <span className={classes.galleryInfoLabel}>Dimensions:</span>
          <span className={classes.galleryInfoValue}>{dimensions}</span>
        </div>
      </Grid>
      <Grid
        item
        sm={mode === FILE_INFO_MODE.GALLERY_DIALOG ? 12 : 6}
        md={6}
        className={classes.fileInfo}
      >
        <div className={classes.itemContainer}>
          <span className={classes.galleryInfoLabel}>File Type:</span>
          <span className={classes.galleryInfoValue}>{fileType}</span>
        </div>
        <div className={classes.itemContainer}>
          <span className={classes.galleryInfoLabel}>File Size:</span>
          <span className={classes.galleryInfoValue}>{fileSize}</span>
        </div>
      </Grid>
      {renderSetProductButton()}
      {renderReplaceFileButton()}
    </Grid>
  )

  const renderMetadataForm = () => (
    <div className={clsx(classes.metadataContainer, 'customized-scrollbar')}>
      <Formik
        initialValues={fileMetadata}
        enableReinitialize
        onSubmit={onSubmitForm}
      >
        {({ values, setFieldValue }) => (
          <Form id="editFileMetadata" onKeyDown={onKeyDown}>
            <Field.Input
              name={`${id || id_select}.alternative_text`}
              legend="Alternative Text"
              inputProps={{
                disabled: isReadOnlyMetadata('alternative_text'),
              }}
            />
            <br />
            {FILE_INFO_MODE.MEDIA_LIBRARY ? (
              <TextInput
                name={`${id || id_select}.title`}
                legend="Title"
                value={values[id || id_select]?.title}
                onChange={(e) => {
                  const textVal = e.target.value
                  if (textVal === '') {
                    return setFieldValue(
                      `${id || id_select}.title`,
                      fileMetadata[id || id_select]?.title,
                    )
                  }
                  return setFieldValue(`${id || id_select}.title`, textVal)
                }}
                inputProps={{
                  disabled: isReadOnlyMetadata('title'),
                }}
              />
            ) : (
              <Field.Input
                name={`${id || id_select}.title`}
                legend="Title"
                inputProps={{
                  disabled: isReadOnlyMetadata('title'),
                }}
              />
            )}
            <br />
            <Field.Input
              name={`${id || id_select}.caption`}
              legend="Caption"
              multiline
              rows={4}
              inputProps={{
                disabled: isReadOnlyMetadata('caption'),
              }}
            />
            <br />
            <Field.Input
              name={`${id || id_select}.description`}
              legend="Description"
              multiline
              rows={4}
              inputProps={{
                disabled: isReadOnlyMetadata('description'),
              }}
            />
            <br />
            <TextInput
              name={`${id || id_select}.copy_link`}
              legend="Copy link"
              value={file.src}
              inputProps={{
                disabled: true,
              }}
            />
            <br />
            <FormLabel component="legend" className={classes.formLabel}>
              Tags
            </FormLabel>
            <InputTag
              tags={values[id || id_select]?.tags || []}
              onTagsChange={(tags) => {
                setFieldValue(
                  `${id || id_select}.tags`,
                  Array.from(new Set(tags)),
                )
              }}
              disabledInput={isReadOnlyMetadata('tags')}
              disabledTag={isReadOnlyMetadata('tags')}
            />
          </Form>
        )}
      </Formik>
    </div>
  )

  return (
    <>
      <Grid
        item
        lg={12}
        sm={mode === FILE_INFO_MODE.GALLERY_DIALOG ? 5 : 6}
        md={mode === FILE_INFO_MODE.GALLERY_DIALOG ? 5 : 6}
        className={clsx(classes.infoContainer, 'customized-scrollbar')}
      >
        {renderInfo()}
      </Grid>
      <Grid
        item
        lg={12}
        sm={mode === FILE_INFO_MODE.GALLERY_DIALOG ? 7 : 6}
        md={mode === FILE_INFO_MODE.GALLERY_DIALOG ? 7 : 6}
        className={clsx(
          classes.metadataGrid,
          mode === FILE_INFO_MODE.GALLERY_DIALOG && 'galleryLayout',
        )}
      >
        {renderMetadataForm()}
      </Grid>
    </>
  )
}

FileInfo.defaultProps = {
  renderSetProductButton: () => {},
  renderReplaceFileButton: () => {},
  didUpdatedFileMetadata: () => {},
  enabledEditMetadataIds: [],
}

FileInfo.propTypes = {
  classes: PropTypes.shape().isRequired,
  file: PropTypes.oneOfType([PropTypes.object, PropTypes.shape()]).isRequired,
  listFileSelected: PropTypes.oneOfType([PropTypes.array, PropTypes.shape()])
    .isRequired,
  dimensions: PropTypes.string.isRequired,
  renderSetProductButton: PropTypes.func,
  didUpdatedFileMetadata: PropTypes.func,
  enabledEditMetadataIds: PropTypes.oneOfType([
    PropTypes.array,
    PropTypes.shape(),
  ]),
  renderReplaceFileButton: PropTypes.func,
  mode: PropTypes.string.isRequired,
  displayName: PropTypes.bool.isRequired,
}

export default withStyles(style)(FileInfo)
